import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box, Typography, Paper, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Button, TextField, FormControlLabel, Switch,
  Alert, Snackbar,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import html2canvas from "html2canvas";
import DownloadIcon from "@mui/icons-material/Download";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const D = {
  blue: "#1565C0",
  blueLight: "#1976D2",
  bluePale: "#E8F0FE",
  green: "#16a34a",
  greenPale: "#F0FDF4",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFD",
  surfaceDeep: "#F1F5F9",
  border: "#E4EBF5",
  borderStrong: "rgba(21,101,192,0.18)",
  text: "#0D1B3E",
  textMid: "#3A4A6B",
  textSoft: "#8898AA",
  textAccent: "#1565C0",
  radius: "12px",
  radiusSm: "8px",
  shadow: "0 1px 3px rgba(10,15,46,0.06), 0 1px 2px rgba(10,15,46,0.04)",
  shadowMd: "0 4px 20px rgba(10,15,46,0.08), 0 1px 4px rgba(10,15,46,0.05)",
};

const ACT_COLORS = {
  "acondicion": "#06b6d4",
  "perforac": "#1e3a8a",
  "almuerzo": "#f97316",
  "extracci": "#7c3aed",
  "introducci": "#db2777",
  "maniobra": "#9333ea",
  "descanso": "#94a3b8",
};
const actColor = (name = "") => {
  const n = name.toLowerCase();
  for (const [k, v] of Object.entries(ACT_COLORS)) if (n.includes(k)) return v;
  return "#64748b";
};

const parseNum = (v) => parseFloat(String(v || "").replace(",", ".")) || 0;
const t2m = (t = "") => { if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + (m || 0); };
const m2h = (m) => { if (m === null || isNaN(m)) return ""; const hh = Math.floor(m / 60) % 24, mm = m % 60; return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`; };
const toHM = (m) => { const h = Math.floor(m / 60), mm = m % 60; return h > 0 ? `${h}h${mm > 0 ? ` ${mm}m` : ""}` : `${mm}m`; };
const parseFecha = (s) => { if (!s) return null; const f = dayjs(s, ["D/M/YYYY", "DD/MM/YYYY", "YYYY-MM-DD"], true); return f.isValid() ? f : null; };

const renderConNegrita = (texto, placeholder = "") => {
  if (!texto) return <span style={{ color: "#aaa" }}>{placeholder}</span>;
  return texto.split(/(\*\*[^*]+\*\*)/g).map((parte, i) =>
    parte.startsWith("**") && parte.endsWith("**")
      ? <strong key={i}>{parte.slice(2, -2)}</strong>
      : <span key={i}>{parte}</span>
  );
};

const DlBtn = ({ onClick }) => (
  <Button onClick={onClick} sx={{
    position: "absolute", top: 12, right: 12,
    minWidth: "auto", p: "5px 8px",
    bgcolor: D.surface,
    border: `1px solid ${D.border}`,
    borderRadius: "7px",
    color: D.textSoft,
    zIndex: 3,
    opacity: 0,
    transition: "opacity 0.2s, color 0.2s, border-color 0.2s",
    ".card-root:hover &": { opacity: 1 },
    "&:hover": { color: D.blue, borderColor: D.blue, bgcolor: D.bluePale },
  }}>
    <DownloadIcon sx={{ fontSize: 14 }} />
  </Button>
);

const Card = ({ children, sx = {}, refProp, noPad, accent }) => (
  <Paper
    elevation={0}
    ref={refProp}
    className="card-root"
    sx={{
      position: "relative",
      p: noPad ? 0 : "20px 24px",
      borderRadius: D.radius,
      border: `1px solid ${D.border}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
      transition: "box-shadow 0.2s, border-color 0.2s",
      "&:hover": { boxShadow: D.shadowMd, borderColor: D.borderStrong },
      ...(accent ? { borderTop: `3px solid ${D.blue}` } : {}),
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SH = ({ title, sub }) => (
  <Box mb={2}>
    <Typography sx={{ fontWeight: 700, fontSize: "1.10rem", color: D.text, lineHeight: 1.3, letterSpacing: "-0.01em" }}>
      {title}
    </Typography>
    {sub && (
      <Typography sx={{ fontSize: "0.96rem", color: D.textSoft, mt: "4px", lineHeight: 1.6 }}>
        {sub}
      </Typography>
    )}
  </Box>
);

const HDivider = () => (
  <Box sx={{ borderBottom: `1px solid ${D.border}`, mb: 2, mt: 0.5 }} />
);

const AlertEditor = React.memo(({ initialData, onSave, pozoActivo }) => {
  const { t } = useTranslation();
  const [localData, setLocalData] = useState(initialData);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => { setLocalData(initialData); }, [initialData.active]);

  const mensajeActual = pozoActivo && (localData.alertasPorPozo?.[pozoActivo] || "");
  const activoActual = pozoActivo && (localData.activoPorPozo?.[pozoActivo] ?? false);

  const handleActivoChange = (v) => {
    if (pozoActivo) setLocalData(prev => ({ ...prev, activoPorPozo: { ...prev.activoPorPozo, [pozoActivo]: v } }));
    else setLocalData(prev => ({ ...prev, active: v }));
  };
  const handleMensajeChange = (v) => {
    if (pozoActivo) setLocalData(prev => ({ ...prev, alertasPorPozo: { ...prev.alertasPorPozo, [pozoActivo]: v } }));
    else setLocalData(prev => ({ ...prev, message: v }));
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/alert`, localData, { withCredentials: true });
      onSave(localData);
      setSnackbar({ open: true, message: t("dashboard.alert_savedNew"), severity: "success" });
    } catch {
      setSnackbar({ open: true, message: t("dashboard.alert_errorNew"), severity: "error" });
    } finally { setSaving(false); }
  };

  return (
    <Paper elevation={0} sx={{
      p: { xs: 3, md: 4 },
      borderRadius: D.radius,
      border: `1px solid ${D.border}`,
      bgcolor: D.surface,
      boxShadow: D.shadow,
      mx: "auto", mb: 4, position: "relative",
    }}>
      <Box mb={3}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: D.blue, mb: 0.5 }}>
          {t("dashboard.titleAlert")}
        </Typography>
        <Typography variant="body2" sx={{ color: D.textSoft, fontSize: "0.82rem" }}>
          {t("dashboard.subtitle")}
        </Typography>
      </Box>

      <Box sx={{ mb: 3, p: 2.5, backgroundColor: "#fff7e6", borderLeft: "4px solid #ffa726", borderRadius: "0 10px 10px 0", display: "flex", gap: 2, alignItems: "flex-start" }}>
        <ChatBubbleOutlineIcon sx={{ color: "#444", fontSize: 20, mt: 0.2 }} />
        <Box sx={{ width: "100%" }}>
          <Typography variant="body2" sx={{ color: D.text, fontWeight: 700, mb: 0.5, fontSize: "0.875rem" }}>
            {t("dashboard.activeAlertMessage")}
          </Typography>
          <Typography variant="body2" component="div" sx={{ color: D.textMid, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.875rem" }}>
            {renderConNegrita(mensajeActual, t("dashboard.empty"))}
          </Typography>
        </Box>
      </Box>

      <TextField
        label={pozoActivo ? t("dashboard.messageLabel", { pozo: pozoActivo }) : t("dashboard.messageLabel", "Reporte global")}
        multiline minRows={6} fullWidth variant="outlined"
        value={mensajeActual}
        onChange={(e) => handleMensajeChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Tab") {
            e.preventDefault();
            const s = e.target.selectionStart, en = e.target.selectionEnd;
            handleMensajeChange(mensajeActual.substring(0, s) + "\t" + mensajeActual.substring(en));
          }
        }}
        sx={{
          mb: 1,
          "& .MuiOutlinedInput-root": {
            backgroundColor: D.surfaceAlt, borderRadius: D.radiusSm, fontSize: "0.875rem",
            "& fieldset": { borderColor: D.border },
            "&:hover fieldset": { borderColor: D.blue },
            "&.Mui-focused fieldset": { borderColor: D.blue },
          },
          "& .MuiInputLabel-root": { fontSize: "0.875rem" },
          "& .MuiInputLabel-root.Mui-focused": { color: D.blue },
        }}
      />
      <Typography variant="caption" sx={{ color: D.textSoft, mb: 3, display: "block", fontSize: "0.72rem" }}>
        {t("dashboard.boldTip")}
      </Typography>

      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{
        p: "12px 16px", backgroundColor: D.surfaceAlt,
        borderRadius: D.radiusSm, border: `1px solid ${D.border}`,
      }}>
        <FormControlLabel
          control={<Switch checked={activoActual} onChange={(e) => handleActivoChange(e.target.checked)} color="warning" />}
          label={<Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem", color: D.textMid }}>{t("dashboard.switchLabel")}</Typography>}
        />
        <Button
          variant="contained" color="warning" onClick={handleSave}
          disabled={saving || !mensajeActual.trim()}
          sx={{ px: 4, fontWeight: 700, textTransform: "none", borderRadius: D.radiusSm, fontSize: "0.875rem" }}
        >
          {saving ? t("dashboard.saving") : t("dashboard.saveButton")}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open} autoHideDuration={3000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled"
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          sx={{ borderRadius: D.radiusSm, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
});

const Gantt = ({ actividades, ganttAxisLabel, tooltipDuracionLabel, actNames }) => {
  const translateAct = (name = "") => {
    const n = name.toLowerCase();
    for (const [k, v] of Object.entries(actNames || {})) {
      if (n.includes(k)) return v;
    }
    return name;
  };
  const containerRef = useRef(null);
  const [cWidth, setCWidth] = useState(0);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setCWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const allM = actividades.flatMap(a => [t2m(a.inicio), t2m(a.fin)]).filter(v => v !== null);
  if (!allM.length || cWidth === 0) return <Box ref={containerRef} sx={{ width: "100%", minHeight: 40 }} />;

  const minT = Math.min(...allM);
  const maxT = Math.max(...allM);
  const span = maxT - minT || 1;

  const TICK_LBL_W = 28;
  const PAD_L = TICK_LBL_W;
  const PAD_R = TICK_LBL_W;
  const LW = 110;
  const GAP = 10;
  const CHART_X = LW + GAP;
  const CHART_W = cWidth - CHART_X - PAD_R;
  const ROW_H = 26;
  const ROW_GAP = 5;
  const AXIS_H = 32;
  const svgH = actividades.length * (ROW_H + ROW_GAP) + AXIS_H;

  const barAreaW = CHART_W - PAD_L;
  const maxTicks = Math.max(2, Math.floor(barAreaW / 48));
  const intervals = [30, 60, 90, 120, 180, 240];
  const tickStep = intervals.find(iv => Math.ceil(span / iv) + 1 <= maxTicks) ?? 240;
  const ticks = Array.from(
    { length: Math.ceil(span / tickStep) + 1 },
    (_, i) => minT + i * tickStep
  ).filter(t => t <= maxT);

  const xOf = (m) => CHART_X + PAD_L + ((m - minT) / span) * barAreaW;
  const tickLabelX = (t, idx) => {
    if (idx === 0) return CHART_X + PAD_L;
    if (idx === ticks.length - 1) return CHART_X + CHART_W;
    return xOf(t);
  };
  const tickAnchor = (idx) => {
    if (idx === 0) return "start";
    if (idx === ticks.length - 1) return "end";
    return "middle";
  };

  const TW = 200, TH = 72, TR = 8;

  return (
    <Box ref={containerRef} sx={{ width: "100%", position: "relative", mt: 0.5 }}>
      <svg width={cWidth} height={svgH} style={{ display: "block", overflow: "visible" }}>
        {actividades.map((act, i) => {
          const s = t2m(act.inicio), e = t2m(act.fin);
          if (s === null || e === null) return null;
          const color = actColor(act.actividad);
          const y = i * (ROW_H + ROW_GAP);
          const bx = xOf(s);
          const bw = Math.max(xOf(e) - xOf(s), 4);
          const isHov = tooltip?.idx === i;
          return (
            <g key={i}>
              <text x={LW} y={y + ROW_H / 2 + 4} textAnchor="end" fontSize="14"
                fill={isHov ? D.blue : D.textMid} fontFamily="sans-serif" fontWeight={isHov ? "700" : "500"}>
                {i + 1}. {(() => { const n = translateAct(act.actividad); return n.length > 16 ? n.slice(0, 12) + "…" : n; })()}
              </text>
              <rect x={CHART_X + PAD_L} y={y + 2} width={CHART_W - PAD_L} height={ROW_H - 4} rx={5} fill={D.surfaceDeep} />
              {ticks.map(t => (
                <line key={t} x1={xOf(t)} x2={xOf(t)} y1={y + 2} y2={y + ROW_H - 2} stroke={D.border} strokeWidth={1} />
              ))}
              <rect x={bx} y={y + 4} width={bw} height={ROW_H - 8} rx={4}
                fill={color} opacity={isHov ? 1 : 0.88}
                style={{ cursor: "pointer", transition: "opacity 0.15s, filter 0.15s", filter: isHov ? "brightness(1.1)" : "none" }}
                onMouseEnter={() => setTooltip({ idx: i, bx, bw, y, act, color })}
                onMouseLeave={() => setTooltip(null)}
              />
            </g>
          );
        })}
        {(() => {
          const ay = actividades.length * (ROW_H + ROW_GAP) + 2;
          return (
            <>
              <line x1={CHART_X + PAD_L} x2={CHART_X + CHART_W} y1={ay} y2={ay} stroke={D.border} strokeWidth={1} />
              {ticks.map((t, idx) => (
                <g key={t}>
                  <line x1={xOf(t)} x2={xOf(t)} y1={ay} y2={ay + 4} stroke={D.textSoft} strokeWidth={1} />
                  <text x={tickLabelX(t, idx)} y={ay + 16} textAnchor={tickAnchor(idx)} fontSize="12" fill={D.textSoft} fontFamily="'Roboto Mono',monospace">
                    {m2h(t)}
                  </text>
                </g>
              ))}
              <text x={CHART_X + PAD_L + (CHART_W - PAD_L) / 2} y={ay + 28} textAnchor="middle" fontSize="10" fill={D.textSoft} fontFamily="sans-serif" letterSpacing="1">
                {ganttAxisLabel}
              </text>
            </>
          );
        })()}
        {tooltip && (() => {
          const rawX = tooltip.bx + tooltip.bw / 2 - TW / 2;
          const tx = Math.max(CHART_X, Math.min(rawX, cWidth - TW - 4));
          const tyAbove = tooltip.y - TH - 10;
          const ty = tyAbove < 2 ? 2 : tyAbove;
          const s = t2m(tooltip.act.inicio), e = t2m(tooltip.act.fin);
          const durMin = (s !== null && e !== null) ? e - s : null;
          return (
            <g style={{ pointerEvents: "none" }}>
              <rect x={tx + 3} y={ty + 3} width={TW} height={TH} rx={TR} fill="rgba(10,15,46,0.14)" />
              <rect x={tx} y={ty} width={TW} height={TH} rx={TR} fill="#fff" stroke={D.border} strokeWidth={1.5} />
              <clipPath id="tt-clip"><rect x={tx} y={ty} width={TW} height={TH} rx={TR} /></clipPath>
              <rect x={tx} y={ty} width={TW} height={5} fill={tooltip.color} clipPath="url(#tt-clip)" />
              <text x={tx + 12} y={ty + 22} fontSize="14.5" fontWeight="700" fill={D.text} fontFamily="sans-serif">
                {translateAct(tooltip.act.actividad)}
              </text>
              <text x={tx + 12} y={ty + 39} fontSize="12.5" fill={D.textMid} fontFamily="'Roboto Mono', monospace">
                {tooltip.act.inicio} → {tooltip.act.fin}
              </text>
              <text x={tx + 12} y={ty + 55} fontSize="12.5" fill={D.textSoft} fontFamily="sans-serif">
                {durMin !== null ? `${tooltipDuracionLabel}: ${toHM(durMin)}` : ""}
                {tooltip.act.total ? `  ·  ${tooltip.act.total}` : ""}
              </text>
            </g>
          );
        })()}
      </svg>
    </Box>
  );
};

const Donut = ({ actividades, totalLabel, actNames }) => {
  const [hovered, setHovered] = useState(null);
  const translateAct = (name = "") => {
    const n = name.toLowerCase();
    for (const [k, v] of Object.entries(actNames || {})) {
      if (n.includes(k)) return v;
    }
    return name;
  };
  const grouped = {};
  actividades.forEach(a => {
    const s = t2m(a.inicio), e = t2m(a.fin);
    if (s === null || e === null) return;
    const orig = a.actividad;
    if (!grouped[orig]) grouped[orig] = 0;
    grouped[orig] += (e - s);
  });
  const entries = Object.entries(grouped);
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  const SIZE = 280, R = 110, cx = SIZE / 2, cy = SIZE / 2;
  let cum = -90;
  const polar = (a, r) => ({ x: cx + r * Math.cos((a * Math.PI) / 180), y: cy + r * Math.sin((a * Math.PI) / 180) });
  const arc = (start, angle, r) => {
    if (angle >= 360) angle = 359.99;
    const s = polar(start, r), e = polar(start + angle, r);
    return `M ${cx} ${cy} L ${s.x} ${s.y} A ${r} ${r} 0 ${angle > 180 ? 1 : 0} 1 ${e.x} ${e.y} Z`;
  };
  const slices = entries.map(([origName, mins]) => {
    const pct = mins / total, angle = pct * 360, startAngle = cum;
    cum += angle;
    return { name: translateAct(origName), origName, mins, pct, startAngle, angle, color: actColor(origName) };
  });
  return (
    <Box sx={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap", justifyContent: "center", mt: 1.5 }}>
      <Box sx={{ position: "relative", flexShrink: 0 }}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} style={{ display: "block" }}>
          <circle cx={cx} cy={cy} r={R + 5} fill="none" stroke={D.border} strokeWidth={1} />
          {slices.map((s, i) => (
            <path key={i} d={arc(s.startAngle, s.angle, R)} fill={s.color}
              opacity={hovered && hovered.name !== s.name ? 0.45 : 0.93}
              style={{ cursor: "pointer", transition: "opacity 0.18s" }}
              onMouseEnter={() => setHovered(s)} onMouseLeave={() => setHovered(null)}
            />
          ))}
          <circle cx={cx} cy={cy} r={R - 38} fill={D.surface} />
          {hovered ? (
            <>
              <text x={cx} y={cy - 14} textAnchor="middle" fontSize="14" fill={D.textSoft} fontFamily="sans-serif">{hovered.name}</text>
              <text x={cx} y={cy + 6} textAnchor="middle" fontSize="18" fill={hovered.color} fontWeight="800" fontFamily="sans-serif">{toHM(hovered.mins)}</text>
              <text x={cx} y={cy + 22} textAnchor="middle" fontSize="14" fill={D.textSoft} fontFamily="sans-serif">{(hovered.pct * 100).toFixed(1)}%</text>
            </>
          ) : (
            <>
              <text x={cx} y={cy - 8} textAnchor="middle" fontSize="14" fill={D.textSoft} fontFamily="sans-serif" letterSpacing="0.5">{totalLabel}</text>
              <text x={cx} y={cy + 14} textAnchor="middle" fontSize="20" fill={D.text} fontWeight="800" fontFamily="sans-serif">{toHM(total)}</text>
            </>
          )}
        </svg>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {slices.map((s, i) => (
          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: "10px", opacity: hovered && hovered.name !== s.name ? 0.4 : 1, transition: "opacity 0.18s", cursor: "default" }}
            onMouseEnter={() => setHovered(s)} onMouseLeave={() => setHovered(null)}>
            <Box sx={{ width: 13, height: 13, borderRadius: "3px", bgcolor: s.color, flexShrink: 0, boxShadow: `0 1px 4px ${s.color}66` }} />
            <Box>
              <Typography sx={{ fontSize: "0.98rem", color: D.textMid, fontWeight: 600, lineHeight: 1.2 }}>{s.name}</Typography>
              <Typography sx={{ fontSize: "0.85rem", color: D.textSoft }}>{toHM(s.mins)} · {(s.pct * 100).toFixed(1)}%</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const KPIChip = ({ label, value, unit, desc, color }) => (
  <Box sx={{
    p: "22px 18px", minHeight: 130,
    border: `1px solid ${D.border}`, borderRadius: D.radiusSm,
    bgcolor: D.surfaceAlt, height: "100%", boxSizing: "border-box",
    display: "flex", flexDirection: "column", justifyContent: "space-between",
    position: "relative", overflow: "hidden",
    transition: "border-color 0.2s, box-shadow 0.2s, background-color 0.2s",
    "&:hover": { borderColor: D.borderStrong, boxShadow: D.shadow, bgcolor: "#fff" },
    "&::before": color ? { content: '""', position: "absolute", top: 0, left: 0, bottom: 0, width: "4px", bgcolor: color } : {},
  }}>
    <Typography sx={{ fontSize: "0.86rem", fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.9px", lineHeight: 1, mb: "10px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "2.2rem", fontWeight: 800, fontFamily: "'DM Mono', 'Roboto Mono', monospace", color: D.text, lineHeight: 1, letterSpacing: "-0.03em", flexGrow: 1, display: "flex", alignItems: "center" }}>
      {value}
      {unit && (
        <Typography component="span" sx={{ fontSize: "0.96rem", fontWeight: 500, color: D.textSoft, ml: "4px", letterSpacing: "0", alignSelf: "flex-end", mb: "3px" }}>
          {unit}
        </Typography>
      )}
    </Typography>
    <Typography sx={{ fontSize: "0.96rem", color: D.textSoft, lineHeight: 1.4, mt: "8px" }}>{desc}</Typography>
  </Box>
);

const KPI_UltimaActividad = () => {
  const { wellId } = useParams();
  const { t } = useTranslation();
  const { auth } = useAuth();

  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPozos, setAllPozos] = useState([]);
  const [pozoFiltro, setPozoFiltro] = useState([]);
  const [alertData, setAlertData] = useState({ active: false, alertasPorPozo: {} });

  const r = {
    cab: useRef(null),
    consumos: useRef(null),
    personal: useRef(null),
    indicadores: useRef(null),
    obsTec: useRef(null),
    obsGeo: useRef(null),
    gantt: useRef(null),
    donut: useRef(null),
  };

  const dl = async (ref, name) => {
    if (!ref.current) return;
    const btns = ref.current.querySelectorAll("button");
    btns.forEach(b => (b.style.display = "none"));
    await new Promise(res => setTimeout(res, 60));
    const canvas = await html2canvas(ref.current, { backgroundColor: "#ffffff" });
    btns.forEach(b => (b.style.display = ""));
    const a = document.createElement("a");
    a.download = `${name}.png`;
    a.href = canvas.toDataURL();
    a.click();
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [alertRes, dataRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/alert`, { withCredentials: true }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/ocr-results-db`, { withCredentials: true, headers }),
        ]);

        setAlertData(alertRes.data || { active: false });

        const partesDiarios = dataRes.data.filter(doc => (doc.categoria || "partes_diarios") === "partes_diarios");

        const parsed = partesDiarios.map(doc => {
          const c = doc.campos || {};
          return {
            id: doc._id,
            fecha: (() => { const f = parseFecha(c.fecha); return f ? f.toDate() : null; })(),
            fechaStr: c.fecha || "",
            turno: c.turno || "",
            proyecto: c.proyecto || "",
            pozo: c.pozo || "",
            supervisor: c.supervisor || "",
            perforista: c.perforista || "",
            geologo: c.geologo || "",
            prof_inicial: parseNum(c.prof_inicial),
            prof_final: parseNum(c.prof_final),
            metros_perf: parseNum(c.metros_perf || c.prof_total),
            hs_perf: c.hs_perf || "",
            gasoil_perforadora: parseNum(c.gasoil_perforadora_lts),
            gasoil_generador: parseNum(c.gasoil_generador_lts),
            gasoil_total: parseNum(c.gasoil_total_lts || c.gasoil),
            nafta: parseNum(c.nafta),
            agua_total: parseNum(c.agua_total_lts || c.agua),
            agua_viajes: parseNum(c.agua_viajes),
            obs_tecnicas: c.observaciones_tecnicas || "",
            obs_geo: c.observaciones_geologicas || "",
            actividades: c.desarrollo_trabajo?.actividades || [],
            total_hs: c.desarrollo_trabajo?.total_hs || 0,
          };
        });

        setAllData(parsed);
        let pozos = [...new Set(parsed.map(d => d.pozo).filter(Boolean))];
        if (wellId && !pozos.includes(wellId)) pozos = [wellId, ...pozos];
        setAllPozos(pozos);
        setPozoFiltro(wellId ? [wellId] : pozos.slice(0, 1));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [wellId]);

  const p = useMemo(() => {
    const filtrado = pozoFiltro.length > 0 ? allData.filter(d => pozoFiltro.includes(d.pozo)) : allData;
    if (!filtrado.length) return null;
    return filtrado.reduce((best, cur) => {
      if (!best || !best.fecha) return cur;
      if (!cur.fecha) return best;
      return cur.fecha > best.fecha ? cur : best;
    }, null);
  }, [allData, pozoFiltro]);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
      <CircularProgress size={32} sx={{ color: D.blue }} />
    </Box>
  );

  const consumosItems = [
    { label: t("KPI.consumosLabelGasoilPerfNew"), value: String(p?.gasoil_perforadora ?? 0), unit: "l", desc: t("KPI.consumosDescGasoilPerfNew"), color: "#1565C0" },
    { label: t("KPI.consumosLabelGasoilGenNew"), value: String(p?.gasoil_generador ?? 0), unit: "l", desc: t("KPI.consumosDescGasoilGenNew"), color: "#1976D2" },
    { label: t("KPI.consumosLabelGasoilTotalNew"), value: String(p?.gasoil_total ?? 0), unit: "l", desc: t("KPI.consumosDescGasoilTotalNew"), color: "#0D47A1" },
    // { label: t("KPI.consumosLabelNaftaNew"),       value: String(p?.nafta || 0),                               unit: "l", desc: t("KPI.consumosDescNaftaNew"),       color: "#f97316" },
    { label: t("KPI.consumosLabelAguaNew"), value: p?.agua_total ? p.agua_total.toLocaleString() : "0", unit: "l", desc: t("KPI.consumosDescAguaNew"), color: "#06b6d4" },
    { label: t("KPI.consumosLabelViajesNew"), value: String(p?.agua_viajes || 0), unit: "", desc: t("KPI.consumosDescViajesNew"), color: "#0891b2" },
  ];

  return (
    <Box sx={{ maxWidth: 1460, mx: "auto" }}>

      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="h4" sx={{
          fontWeight: 900, color: D.text, letterSpacing: "-0.05em", mb: 1,
          px: 1.5, py: 0.5, borderLeft: "6px solid #1976d2",
          backgroundColor: "rgba(25,118,210,0.08)", borderRadius: "0 8px 8px 0",
          display: "inline-flex", alignItems: "center",
        }}>
          {t("KPI.pageTitleNew")}
        </Typography>
        <Typography sx={{ fontSize: "0.98rem", color: D.textSoft, letterSpacing: "0.01em" }}>
          {t("KPI.pageSubtitleNew")}
        </Typography>
      </Box>

      {auth?.role === "superAdmin" && (
        <AlertEditor initialData={alertData} onSave={(u) => setAlertData(u)} pozoActivo={wellId || null} />
      )}

      {(() => {
        const activo = wellId && (alertData.activoPorPozo?.[wellId] ?? false);
        const msg = wellId ? alertData.alertasPorPozo?.[wellId] : null;
        if (!activo || !msg) return null;
        return (
          <Box sx={{
            width: "100%", mt: 2, mb: 4, p: "14px 18px",
            backgroundColor: "#fff7e6", borderLeft: "4px solid #ffa726",
            borderRadius: "0 10px 10px 0", display: "flex", gap: 2,
            alignItems: "flex-start", boxShadow: "0 2px 8px rgba(255,167,38,0.1)",
          }}>
            <ChatBubbleOutlineIcon sx={{ color: "#5d4037", fontSize: 20, mt: 0.3, flexShrink: 0 }} />
            <Box sx={{ width: "100%" }}>
              <Typography variant="body2" sx={{ color: D.text, fontWeight: 700, mb: 0.5, fontSize: "0.875rem" }}>
                {t("dashboard.activeAlertMessage")}
                {wellId && (
                  <Box component="span" sx={{ ml: 1, fontSize: "0.72rem", color: D.textSoft, fontWeight: 500 }}>
                    — {wellId}
                  </Box>
                )}
              </Typography>
              <Typography variant="body2" component="div" sx={{ color: D.textMid, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.875rem" }}>
                {renderConNegrita(msg)}
              </Typography>
            </Box>
          </Box>
        );
      })()}

      {!p ? (
        <Box sx={{ mt: 8, textAlign: "center" }}>
          <Typography sx={{ color: D.textSoft, fontSize: "0.82rem" }}>
            {t("KPI.noDataNew")}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexShrink: 0 }}>
            <Box sx={{
              display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 0.75,
              px: 1.5, py: 0.5, bgcolor: "#1565C0", borderRadius: "8px", flexShrink: 0,
            }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: "50%", bgcolor: D.blue,
                boxShadow: "0 0 6px rgba(0,229,255,0.9)",
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.5, transform: "scale(1.4)" },
                },
              }} />
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: "#fff", letterSpacing: 1, textTransform: "uppercase" }}>
                {t("KPI.pozo")}
              </Typography>
            </Box>

            <FormControl disabled sx={{
              minWidth: { xs: "100%", sm: 200 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px", fontSize: "1rem", fontWeight: 700,
                height: 52, bgcolor: "#fff", border: `2px solid ${D.blue}`,
                "& fieldset": { border: "none" },
                "&:hover": { bgcolor: "#f0f4ff" },
              },
              "& .MuiSelect-select": { color: D.text },
            }}>
              <Select
                value={pozoFiltro[0] || ""}
                onChange={(e) => setPozoFiltro([e.target.value])}
                displayEmpty
              >
                {allPozos.map((pozo) => (
                  <MenuItem key={pozo} value={pozo} sx={{ fontSize: "1.05rem", fontWeight: 600 }}>
                    {pozo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Card refProp={r.cab} accent>
            <DlBtn onClick={() => dl(r.cab, "cabecera")} />
            <SH title={t("KPI.cabHeaderTitleNew")} sub={t("KPI.cabHeaderSubNew")} />
            <HDivider />
            <Box sx={{ display: "flex", mt: 1, gap: 0 }}>
              {[
                { label: t("KPI.cabLabelFechaNew"), value: p.fechaStr, mono: true },
                { label: t("KPI.cabLabelTurnoNew"), value: p.turno, blue: true },
                { label: t("KPI.cabLabelProyectoNew"), value: p.proyecto },
                { label: t("KPI.cabLabelPozoNew"), value: p.pozo, bold: true },
              ].map(({ label, value, mono, blue, bold }, i) => (
                <Box key={label} sx={{
                  flex: 1,
                  pl: i === 0 ? 0 : "24px",
                  borderLeft: i === 0 ? "none" : `1px solid ${D.border}`,
                }}>
                  <Typography sx={{
                    fontSize: "0.58rem", fontWeight: 700, color: D.textSoft,
                    textTransform: "uppercase", letterSpacing: "1px", mb: "8px", lineHeight: 1,
                  }}>
                    {label}
                  </Typography>
                  <Typography sx={{
                    fontSize: mono ? "1.45rem" : "1.35rem",
                    fontWeight: bold || blue ? 900 : 700,
                    fontFamily: mono ? "'DM Mono','Roboto Mono',monospace" : "inherit",
                    color: blue ? D.blue : D.text,
                    letterSpacing: mono ? "-0.03em" : "-0.02em",
                    lineHeight: 1.1,
                  }}>
                    {value || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>

          {p.obs_tecnicas && (
            <Card refProp={r.obsTec}>
              <DlBtn onClick={() => dl(r.obsTec, "obs_tecnicas")} />
              <SH title={t("KPI.obsTecTitleNew")} sub={t("KPI.obsTecSubNew")} />
              <HDivider />
              <Box sx={{ borderLeft: `3px solid ${D.blue}`, pl: "14px", py: "8px", bgcolor: D.bluePale, borderRadius: "0 8px 8px 0" }}>
                <Typography sx={{ fontSize: "0.845rem", color: D.textMid, lineHeight: 1.75 }}>{p.obs_tecnicas}</Typography>
              </Box>
            </Card>
          )}

          {p.obs_geo && (
            <Card refProp={r.obsGeo}>
              <DlBtn onClick={() => dl(r.obsGeo, "obs_geologicas")} />
              <SH title={t("KPI.obsGeoTitleNew")} sub={t("KPI.obsGeoSubNew")} />
              <HDivider />
              <Box sx={{ borderLeft: `3px solid ${D.green}`, pl: "14px", py: "8px", bgcolor: D.greenPale, borderRadius: "0 8px 8px 0" }}>
                <Typography sx={{ fontSize: "0.845rem", color: D.textMid, lineHeight: 1.75 }}>{p.obs_geo}</Typography>
              </Box>
            </Card>
          )}

          <Card refProp={r.indicadores}>
            <DlBtn onClick={() => dl(r.indicadores, "indicadores")} />
            <SH title={t("KPI.indicadoresTitleNew")} sub={t("KPI.indicadoresSubNew")} />
            <HDivider />
            <Box sx={{ display: "flex", gap: "6px", flexWrap: { xs: "wrap", md: "nowrap" } }}>
              {[
                { label: t("KPI.kpiLabelMetrosPerfNew"), value: p.metros_perf.toFixed(2), unit: "m", desc: t("KPI.kpiDescMetrosPerfNew"), color: D.blue },
                { label: t("KPI.kpiLabelHorasPerfNew"), value: p.total_hs ? String(p.total_hs) : p.hs_perf, unit: "h", desc: t("KPI.kpiDescHorasPerfNew"), color: "#16a34a" },
                { label: t("KPI.kpiLabelGasoilTotalKpiNew"), value: String(p.gasoil_total), unit: "l", desc: t("KPI.kpiDescGasoilTotalKpiNew"), color: "#f97316" },
                { label: t("KPI.kpiLabelViajesAguaKpiNew"), value: String(p.agua_viajes || 0), unit: "", desc: t("KPI.kpiDescViajesAguaKpiNew"), color: "#7c3aed" },
              ].map(({ label, value, unit, desc, color }) => (
                <Box key={label} sx={{ flex: "1 1 0", minWidth: 0 }}>
                  <KPIChip label={label} value={value} unit={unit} desc={desc} color={color} />
                </Box>
              ))}
            </Box>
          </Card>

          {p.actividades.length > 0 && (
            <Card refProp={r.gantt}>
              <DlBtn onClick={() => dl(r.gantt, "gantt")} />
              <SH title={t("KPI.ganttTitleNew")} sub={t("KPI.ganttSubNew")} />
              <HDivider />
              <Gantt
                actividades={p.actividades}
                ganttAxisLabel={t("KPI.ganttAxisLabelNew")}
                tooltipDuracionLabel={t("KPI.ganttTooltipDuracionNew")}
                actNames={{
                  "acondicion": t("KPI.actAcondicionamientoNew"),
                  "perforac": t("KPI.actPerforacionNew"),
                  "almuerzo": t("KPI.actAlmuerzoNew"),
                  "extracci": t("KPI.actExtraccionNew"),
                  "introducci": t("KPI.actIntroduccionNew"),
                  "maniobra": t("KPI.actManiobraNew"),
                  "descanso": t("KPI.actDescansoNew"),
                }}
              />
            </Card>
          )}

          {p.actividades.length > 0 && (
            <Card refProp={r.donut}>
              <DlBtn onClick={() => dl(r.donut, "distribucion")} />
              <SH title={t("KPI.donutTitleNew")} sub={t("KPI.donutSubNew")} />
              <HDivider />
              <Donut
                actividades={p.actividades}
                totalLabel={t("KPI.donutLabelTotalNew")}
                actNames={{
                  "acondicion": t("KPI.actAcondicionamientoNew"),
                  "perforac": t("KPI.actPerforacionNew"),
                  "almuerzo": t("KPI.actAlmuerzoNew"),
                  "extracci": t("KPI.actExtraccionNew"),
                  "introducci": t("KPI.actIntroduccionNew"),
                  "maniobra": t("KPI.actManiobraNew"),
                  "descanso": t("KPI.actDescansoNew"),
                }}
              />
            </Card>
          )}

          <Card refProp={r.consumos}>
            <DlBtn onClick={() => dl(r.consumos, "consumos")} />
            <SH title={t("KPI.consumosTitleNew")} sub={t("KPI.consumosSubNew")} />
            <HDivider />
            <Box sx={{ display: "flex", gap: "6px", flexWrap: { xs: "wrap", md: "nowrap" } }}>
              {consumosItems.map(({ label, value, unit, desc, color }) => (
                <Box key={label} sx={{ flex: "1 1 0", minWidth: 0 }}>
                  <KPIChip label={label} value={value} unit={unit} desc={desc} color={color} />
                </Box>
              ))}
            </Box>
          </Card>

          <Card refProp={r.personal}>
            <DlBtn onClick={() => dl(r.personal, "personal")} />
            <SH title={t("KPI.personalTitleNew")} sub={t("KPI.personalSubNew")} />
            <HDivider />
            <Box sx={{ display: "flex", mt: 0.5, gap: 0 }}>
              {[
                { label: t("KPI.personalLabelSupervisorNew"), value: p.supervisor },
                { label: t("KPI.personalLabelPerforistaNew"), value: p.perforista },
                { label: t("KPI.personalLabelGeologoNew"), value: p.geologo },
              ].map(({ label, value }, i) => (
                <Box key={label} sx={{ flex: 1, pl: i === 0 ? 0 : "20px", borderLeft: i === 0 ? "none" : `1px solid ${D.border}` }}>
                  <Typography sx={{ fontSize: "0.58rem", fontWeight: 700, color: D.textSoft, textTransform: "uppercase", letterSpacing: "0.8px", mb: "6px", lineHeight: 1 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: "0.92rem", fontWeight: 800, color: D.text, letterSpacing: "-0.01em" }}>
                    {value || "—"}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>

        </Box>
      )}
    </Box>
  );
};

export default KPI_UltimaActividad;